import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AdminApplications, GraduationApi, errorMessage } from '../services/GraduationApi';
import { formatSubmissionDate } from '../services/SubmissionDate';
import { useCanViewUndergraduateApplications } from './StudentWorkspace';

const columns = [
  { key: 'student', label: 'Student' }, { key: 'graduationTerm', label: 'Graduation term' },
  { key: 'degree', label: 'Degree' }, { key: 'studyArea', label: 'Study Area' },
  { key: 'submitted', label: 'Submit Date' }, { key: 'evaluator', label: 'Evaluator' },
  { key: 'status', label: 'Current status' },
];

const statusLabel = (status: string) => /^Evaluated\s*[-–]\s*with concerns$/i.test(status) ? 'Evaluated - With Concerns' : status;

export default function UndergraduateAdmin() {
  const allowed = useCanViewUndergraduateApplications();
  const [params, setParams] = useSearchParams();
  // Also accepts the student identifier used in legacy bookmarks.
  const identifier = params.get('identifier') || params.get('userId') || '';
  const archived = params.get('archived') === 'true';
  const statusParam = params.get('status');
  const statusValue = statusParam?.trim() ? Number(statusParam) : NaN;
  const statusId = Number.isInteger(statusValue) && statusValue >= 0 ? statusValue : null;
  const pageValue = Number(params.get('page'));
  const page = Number.isInteger(pageValue) && pageValue > 0 && pageValue <= 100000 ? pageValue : 1;
  const requestedSort = params.get('sort') || 'submitted';
  const sort = columns.some(c => c.key === requestedSort) ? requestedSort : 'submitted';
  const direction = params.get('direction') === 'asc' ? 'asc' : 'desc';
  const [input, setInput] = useState(identifier);
  const [data, setData] = useState<AdminApplications | null>(null);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState('');
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  useEffect(() => { setInput(identifier); setValidation(''); }, [identifier]);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    setLoading(true); setError('');
    GraduationApi.adminApplications(identifier, statusId, page, controller.signal, sort, direction, archived)
      .then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [allowed, identifier, statusId, page, revision, sort, direction, archived]);
  if (!allowed) return <Navigate to="/" replace />;
  const update = (id: string, status: number | null, nextPage = 1, nextSort = sort, nextDirection = direction, nextArchived = archived) => {
    const next = new URLSearchParams();
    if (id) next.set('identifier', id);
    if (status !== null) next.set('status', String(status));
    if (nextArchived) next.set('archived', 'true');
    if (nextPage > 1) next.set('page', String(nextPage));
    if (nextSort !== 'submitted' || nextDirection !== 'desc') {
      next.set('sort', nextSort); next.set('direction', nextDirection);
    }
    setParams(next);
  };
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim().toLowerCase();
    if (!value || value.length > 50 || Array.from(value).some(character => character.charCodeAt(0) < 32 || '%_[]'.includes(character))) {
      setValidation('Enter a last name, Star ID, or Tech ID without wildcard characters.'); return;
    }
    setValidation(''); update(value, null, 1, sort, direction, false); setRevision(v => v + 1);
  };
  const clear = () => { setInput(''); setValidation(''); update('', null, 1, sort, direction, false); };
  return <div className="admin-undergraduate">
    <header className="mb-4"><p className="admin-eyebrow">Undergraduate review</p>
      <h1>Undergraduate Applications</h1>
      <p className="text-secondary">Find a student or browse applications by their current status.</p>
    </header>
    {error && <Alert variant="danger" role="alert">{error} <Button variant="outline-danger" size="sm" onClick={() => setRevision(v => v + 1)}>Try again</Button></Alert>}
    {loading && !data && <p role="status">Loading undergraduate applications…</p>}
    <section className="section-panel mb-4" aria-labelledby="student-search-title">
      <h2 id="student-search-title" className="h5">Find a Student</h2>
      <Form onSubmit={search} noValidate>
        <Form.Label htmlFor="student-identifier">Last name, Star ID, or Tech ID</Form.Label>
        <div className="d-flex flex-wrap gap-2 align-items-start">
          <div className="admin-search-input"><Form.Control id="student-identifier" value={input}
            onChange={e => { setInput(e.target.value); setValidation(''); }} autoComplete="off" spellCheck={false}
            placeholder="e.g. Smith, 12345678, or ab1234cd" aria-describedby={validation ? 'search-help search-validation' : 'search-help'} isInvalid={Boolean(validation)} />
            {validation && <Form.Control.Feedback type="invalid" id="search-validation" role="alert">{validation}</Form.Control.Feedback>}</div>
          <Button type="submit">Search</Button>
          <Button variant="outline-secondary" type="button" onClick={clear}>Clear search</Button>
        </div>
        <Form.Text id="search-help">Search by the beginning of a last name, Star ID, or Tech ID. Results include older and archived undergraduate applications.</Form.Text>
      </Form>
    </section>
    {data && <>
      <section aria-labelledby="status-summary-title" className="section-panel mb-4">
        <h2 id="status-summary-title" className="h4">Current Review Queue</h2>
        <p className="text-secondary">Select a status to view non-archived applications submitted in the last two years, or select Archived to view archived applications from all submission dates.</p>
        <div className="admin-status-grid">
          {data.statuses.map(s => <button key={s.statusId} className="admin-status-card"
            aria-pressed={!identifier && !archived && statusId === s.statusId} onClick={() => update('', s.statusId, 1, sort, direction, false)}>
            <span>{statusLabel(s.status)}</span><strong>{s.count.toLocaleString()}</strong><span className="admin-card-action">View applications →</span>
          </button>)}
          <button className="admin-status-card" aria-pressed={!identifier && archived && statusId === null}
            onClick={() => update('', null, 1, sort, direction, true)}>
            <span>Archived</span><strong>{data.archivedCount.toLocaleString()}</strong><span className="admin-card-action">View applications →</span>
          </button>
        </div>
      </section>
      <section className="section-panel" aria-labelledby="application-results-title" aria-busy={loading}>
        <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
          <div><h2 id="application-results-title" className="h4">{identifier ? `Search results for ${identifier}` : archived ? 'Archived' : statusId !== null ? statusLabel(data.statuses.find(s => s.statusId === statusId)?.status || 'Filtered applications') : 'All current applications'}</h2>
            <p className="mb-0 text-secondary" role="status">{loading ? 'Updating undergraduate applications…' : error ? 'Unable to update. Previously loaded applications are shown.' : <>{data.total.toLocaleString()} {data.total === 1 ? 'application' : 'applications'}{(identifier || archived) && ' · All submission dates'}</>}</p></div>
          <Button variant="outline-primary" size="sm" onClick={() => setRevision(v => v + 1)}>Refresh</Button>
        </div>
        {data.applications.length === 0 ? <div className="empty-state">{identifier ? 'No undergraduate applications match this ID. Check the ID and try again.' : 'No applications in this view.'}</div> :
          <AccessibleTable scrollLabel="Undergraduate application results" striped hover className="align-middle">
            <caption className="visually-hidden">Undergraduate applications with degree, study area, submission date, evaluator and current status</caption>
            <thead><tr>{columns.map(column => <th key={column.key} scope="col"
              aria-sort={sort === column.key ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}>
              <button type="button" className="admin-sort-button"
                onClick={() => update(identifier, statusId, 1, column.key, sort === column.key && direction === 'asc' ? 'desc' : 'asc')}
                aria-label={`${column.label}: sort ${sort === column.key && direction === 'asc' ? 'descending' : 'ascending'}`}>
                {column.label} <span aria-hidden="true">{sort === column.key ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
              </button>
            </th>)}<th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>{data.applications.map(a => <tr key={a.applicationId}>
              <td><strong>{a.studentName || 'Name unavailable'}</strong><div className="small text-secondary">Tech ID #{a.techId || 'Not available'}</div></td>
              <td>{a.graduationTerm || 'Not specified'}</td><td>{a.degree || 'Not specified'}</td>
              <td>{a.studyArea || 'Not specified'}</td>
              <td className="text-nowrap"><time dateTime={a.submitDate}>{formatSubmissionDate(a.submitDate, 'short')}</time></td>
              <td>{a.evaluator || 'Unassigned'}</td>
              <td><span className="admin-status-label">{a.status}</span>{a.archived && <div className="small text-secondary mt-1">Archived</div>}</td>
              <td><Link to={`/admin/undergraduate/${a.applicationId}?list=${encodeURIComponent(params.toString())}`} aria-label={`View application ${a.applicationId} for ${a.studentName}`}>View</Link></td>
            </tr>)}</tbody>
          </AccessibleTable>}
        {(data.total > data.pageSize || page > 1) && <nav aria-label="Application result pages" className="d-flex align-items-center justify-content-end gap-3 mt-3">
          <Button variant="outline-secondary" disabled={page === 1} onClick={() => update(identifier, statusId, page - 1)}>Previous</Button>
          <span>Page {page} of {Math.max(1, Math.ceil(data.total / data.pageSize))}</span>
          <Button variant="outline-secondary" disabled={page * data.pageSize >= data.total} onClick={() => update(identifier, statusId, page + 1)}>Next</Button>
        </nav>}
      </section>
    </>}
  </div>;
}
